import { AddBox } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { VendorActivationStatus } from 'entities/Vendors';
import { ColoredLinkBox } from 'shared/ui/ColoredLinkBox';
import { SupplierStatusTooltip, useSupplierActions } from 'shared/ui/Suppliers';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

import { sortSupplierProposals } from '../lib/sort/sortSupplierProposals';

interface SuppliersListProps {
    proposals: Proposal[];
    showSku?: boolean;
    bordered?: boolean;
}

export const SuppliersList = ({
    proposals,
    showSku = false,
    bordered = false,
}: SuppliersListProps) => {
    const { t } = useTranslation();
    const { handleEnableProductOrdering, getProposalStatus, vendorsMap } =
        useSupplierActions();

    const sortedProposals = useMemo(
        () => sortSupplierProposals(proposals ?? [], vendorsMap),
        [proposals, vendorsMap],
    );

    return (
        <>
            {sortedProposals.map((proposal) => {
                const { activationStatus, showEnableButton } =
                    getProposalStatus(proposal);

                return (
                    <Box
                        key={proposal.uid}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: showSku
                                ? 'space-between'
                                : 'flex-start',
                            gap: '8px',
                            padding: showSku ? '16px 0' : '0',
                            borderBottom: bordered
                                ? '1px solid #E9E8E8'
                                : undefined,
                            '&:hover': {
                                '& .enable-product-ordering-button': {
                                    opacity: showEnableButton ? 1 : 0,
                                },
                            },
                        }}
                        data-testclass={'supplier'}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    marginY: showSku ? '0' : '10px',
                                    color: showSku
                                        ? 'rgba(0, 0, 0, 0.5)'
                                        : 'rgba(0, 0, 0, 0.85)',
                                    fontFamily: showSku
                                        ? 'Open Sans'
                                        : undefined,
                                    fontSize: showSku ? '16px' : '14px',
                                    fontWeight: 400,
                                    lineHeight: showSku ? undefined : '20px',
                                }}
                                data-testclass={'name'}
                            >
                                {proposal.seller.name}
                            </Typography>

                            {activationStatus ===
                                VendorActivationStatus.VERIFIED && (
                                <Box sx={{ marginLeft: '10px' }}>
                                    <ColoredLinkBox
                                        data-testclass={
                                            'operations-account-enabled'
                                        }
                                        color="#DBEFE1"
                                        tooltipText={t(
                                            'Product ordering enabled',
                                        )}
                                    />
                                </Box>
                            )}

                            {(activationStatus ===
                                VendorActivationStatus.VERIFYING ||
                                activationStatus ===
                                    VendorActivationStatus.INVALID) && (
                                <Box sx={{ marginLeft: '10px' }}>
                                    <SupplierStatusTooltip
                                        status={activationStatus}
                                    />
                                </Box>
                            )}

                            {showEnableButton && (
                                <IconButton
                                    className="enable-product-ordering-button"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEnableProductOrdering(proposal);
                                    }}
                                    disableRipple
                                    sx={{
                                        height: '24px',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                        padding: '5px 0 0 0',
                                        marginLeft: '7px',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    data-testid="btn-enable-product-ordering"
                                >
                                    <DynamicTooltip
                                        content={
                                            <Typography typography="openSans.caption">
                                                {t('Enable product ordering')}
                                            </Typography>
                                        }
                                    >
                                        <AddBox
                                            sx={{
                                                fontSize: '26px',
                                                color: '#3F6EB7',
                                            }}
                                        />
                                    </DynamicTooltip>
                                </IconButton>
                            )}
                        </Box>
                        {showSku && (
                            <Typography
                                maxWidth="340px"
                                textAlign="right"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.85)',
                                    fontFamily: 'Open Sans',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    lineHeight: '20px',
                                    letterSpacing: '0.15px',
                                }}
                                data-testclass="supplier-list-sku"
                            >
                                {proposal.externalInvId || ''}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </>
    );
};
